import { useMemo } from 'react';
import { YStack } from 'smoothie';

import { AddressTable } from '../../address/components/AddressTable';
import { type MembershipDetails } from '../../entities/entityTypes';
import { MemberPIIRecord } from './MemberPIIRecord';
import { MemberProfileIPQSection } from './MemberProfileIPQSection';
import { MemberProfileMemberTable } from './MemberProfileMemberTable';
import { MembershipFinancialGoalsTable } from './MembershipFinancialGoalsTable';
import { MembershipInsuranceTable } from './MembershipInsuranceTable';
import { MembershipInvestmentsTable } from './MembershipInvestmentsTable';
import { MembershipKidsTable } from './MembershipKidsTable';
import { MembershipRetirementTable } from './MembershipRetirementTable';

interface MemberProfileProps {
    membershipDetails: MembershipDetails;
}

export const MemberProfile = ({ membershipDetails }: MemberProfileProps) => {
    const { users: members, membershipProfileAnswers } = membershipDetails;
    const userAddresses = membershipDetails.users.map((u) => u.userAddress);
    const guidanceSessions = membershipDetails.guidanceSession;

    /**
     * Currently, we do not explicitly track timezone for a member, however,
     * we do receive this information per guidance session from Calendly.
     *
     * Here, we assume the timezone of the most recent session is most accurate.
     * This also assumes both partners are in the same timezone.
     */
    const timeZoneLabel = useMemo(() => {
        const timeZone = guidanceSessions[0]?.attendeeTimezone;

        if (timeZone == null) return '';

        const opts: Intl.DateTimeFormatOptions = {
            timeZoneName: 'short',
            timeZone,
        };

        const tzName = Intl.DateTimeFormat('en-EN', opts)
            .format(Date.now())
            .split(',')[1]
            ?.trim();

        return tzName == null ? timeZone : `${timeZone} (${tzName})`;
    }, [guidanceSessions]);

    return (
        <YStack padding={24} gap={32}>
            <MemberProfileMemberTable
                members={members}
                timezone={timeZoneLabel}
            />
            <MemberPIIRecord members={members} />
            <AddressTable userAddresses={userAddresses} />
            <MembershipKidsTable
                membershipProfileAnswers={membershipProfileAnswers}
            />
            <MembershipFinancialGoalsTable
                membershipProfileAnswers={membershipProfileAnswers}
            />
            <MembershipRetirementTable
                membershipProfileAnswers={membershipProfileAnswers}
            />
            <MembershipInsuranceTable members={members} />
            <MembershipInvestmentsTable members={members} />
            <MemberProfileIPQSection members={members} />
        </YStack>
    );
};
